import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Clock, CheckCircle, AlertTriangle, TrendingUp, ChevronRight, Car, Calendar, XCircle, List } from "lucide-react";
import { ADMIN_BOOKINGS_URL, adminBookingDetailUrl } from "@/constants/adminRoutes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  CONFIRMED: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  IN_PROGRESS: "bg-green-500/15 text-green-400 border-green-500/30",
};

const GAP_STYLES = {
  CLASH: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", icon: "❌", label: "Clash" },
  TIGHT: { bg: "bg-yellow-500/10", border: "border-yellow-500/30", text: "text-yellow-400", icon: "⚠️", label: "Tight" },
  SAFE: { bg: "bg-green-500/10", border: "border-green-500/30", text: "text-green-400", icon: "✅", label: "Safe" },
  UNKNOWN: { bg: "bg-white/5", border: "border-white/10", text: "text-sarathi-text-muted", icon: "—", label: "" },
};

const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <button
    type="button"
    onClick={onClick} className="bg-sarathi-surface border border-white/10 rounded-xl p-4 text-left hover:border-sarathi-primary/30 transition-all w-full">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-sarathi-text-muted text-xs">{label}</p>
        <p className="text-white font-outfit text-2xl font-bold">{value}</p>
      </div>
    </div>
  </button>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/admin/dashboard`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sarathi-text-muted text-center py-20">Loading...</div>;
  if (!data) return <div className="text-red-400 text-center py-20">Failed to load dashboard</div>;

  const vs = data.vehicleStatus || {};

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="admin-dashboard">
      <h1 className="font-outfit text-xl font-bold text-white">Dashboard</h1>

      {/* Failed Notification Warning */}
      {data.failedNotifications > 0 && (
        <button
          type="button"
          onClick={() => navigate(`${ADMIN_BOOKINGS_URL}?tab=PENDING`)}
          className="w-full bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 flex items-center gap-3 text-left hover:bg-yellow-500/15 transition-colors" data-testid="failed-notification-banner">
          <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0" />
          <p className="text-yellow-400 text-sm font-medium">{data.failedNotifications} booking(s) with failed Telegram notifications. Check admin panel manually.</p>
          <ChevronRight className="w-4 h-4 text-yellow-400 shrink-0 ml-auto" />
        </button>
      )}

      {/* Stats Row 1 */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={AlertTriangle} label="Pending" value={data.pending} color="bg-orange-500/15 text-orange-400"
          onClick={() => navigate(`${ADMIN_BOOKINGS_URL}?tab=PENDING`)} />
        <StatCard icon={CheckCircle} label="Confirmed Today" value={data.confirmedToday} color="bg-blue-500/15 text-blue-400"
          onClick={() => navigate(`${ADMIN_BOOKINGS_URL}?tab=CONFIRMED`)} />
        <StatCard icon={Clock} label="In Progress" value={data.inProgress} color="bg-green-500/15 text-green-400"
          onClick={() => navigate(`${ADMIN_BOOKINGS_URL}?tab=IN_PROGRESS`)} />
        <StatCard icon={Calendar} label="Scheduled Today" value={data.scheduledToday || 0} color="bg-purple-500/15 text-purple-400"
          onClick={() => navigate(`${ADMIN_BOOKINGS_URL}?tab=all`)} />
        <StatCard icon={XCircle} label="Declined Today" value={data.declinedToday || 0} color="bg-red-900/15 text-red-300"
          onClick={() => navigate(`${ADMIN_BOOKINGS_URL}?tab=DECLINED`)} />
        <StatCard icon={TrendingUp} label="Total Bookings" value={data.total} color="bg-white/5 text-sarathi-text-secondary"
          onClick={() => navigate(`${ADMIN_BOOKINGS_URL}?tab=all`)} />
      </div>

      {/* Vehicle Status */}
      <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Car className="w-4 h-4 text-sarathi-primary" />
          <h2 className="text-white font-semibold text-sm">Vehicle Status</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-sarathi-background/50 rounded-lg px-3 py-2.5">
            <p className="text-sarathi-text-muted text-xs">Current</p>
            <p className={`font-semibold text-sm ${vs.status === "In Progress" ? "text-green-400" : "text-blue-400"}`}>
              {vs.status || "Available"}
            </p>
          </div>
          <div className="bg-sarathi-background/50 rounded-lg px-3 py-2.5">
            <p className="text-sarathi-text-muted text-xs">Location</p>
            <p className="text-white text-sm truncate">{vs.location || vs.drop || "—"}</p>
          </div>
          <div className="bg-sarathi-background/50 rounded-lg px-3 py-2.5">
            <p className="text-sarathi-text-muted text-xs">Free at</p>
            <p className="text-sarathi-primary font-semibold text-sm">{vs.freeAt || "Now"}</p>
          </div>
          <div className="bg-sarathi-background/50 rounded-lg px-3 py-2.5">
            <p className="text-sarathi-text-muted text-xs">Next Ride</p>
            {vs.nextRide ? (
              <p className="text-white text-sm truncate">{vs.nextRide.time} · {vs.nextRide.pickup?.split(",")[0]}</p>
            ) : (
              <p className="text-sarathi-text-muted text-sm">None</p>
            )}
          </div>
        </div>
      </div>

      {/* Today's Timeline */}
      <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <List className="w-4 h-4 text-sarathi-primary" />
            <h2 className="text-white font-semibold text-sm">Today's Timeline</h2>
          </div>
          <span className="text-sarathi-text-muted text-xs">{data.timeline?.length || 0} ride(s)</span>
        </div>

        {(!data.timeline || data.timeline.length === 0) ? (
          <p className="text-sarathi-text-muted text-sm py-6 text-center">No confirmed rides today</p>
        ) : (
          <div className="space-y-0">
            {data.timeline.map((ride, i) => (
              <div key={ride.id}>
                {/* Ride Card */}
                <Link to={adminBookingDetailUrl(ride.id)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-sarathi-background/50 transition-colors group">
                  {/* Time Column */}
                  <div className="w-20 shrink-0 text-right">
                    <p className="text-sarathi-primary font-mono text-sm font-semibold">{ride.startTime}</p>
                    {ride.endTime && <p className="text-sarathi-text-muted font-mono text-xs">→ {ride.endTime}</p>}
                  </div>

                  {/* Timeline dot + line */}
                  <div className="flex flex-col items-center shrink-0">
                    <div className={`w-3 h-3 rounded-full ${ride.status === "IN_PROGRESS" ? "bg-green-400 animate-pulse" : "bg-blue-400"}`} />
                  </div>

                  {/* Ride Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm truncate">{ride.pickup?.split(",")[0]} → {ride.drop?.split(",")[0]}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full border shrink-0 ${STATUS_COLORS[ride.status] || "bg-white/5 text-white border-white/10"}`}>
                        {ride.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-sarathi-text-muted text-xs">{ride.passengerName}</span>
                      <span className="text-sarathi-text-muted text-xs">·</span>
                      <span className="text-sarathi-text-muted text-xs">{ride.bookingReference}</span>
                      {ride.fare && <span className="text-sarathi-primary text-xs font-semibold ml-auto">{ride.fare}</span>}
                    </div>
                  </div>

                  <ChevronRight className="w-4 h-4 text-sarathi-text-muted group-hover:text-white shrink-0" />
                </Link>

                {/* Gap Indicator */}
                {ride.gap && (
                  <div className="flex items-center gap-3 px-3 py-1.5">
                    <div className="w-20 shrink-0" />
                    <div className="flex flex-col items-center shrink-0">
                      <div className="w-px h-4 bg-white/10" />
                    </div>
                    <div className={`flex items-center gap-2 text-xs rounded-md px-2.5 py-1 border ${GAP_STYLES[ride.gap.status]?.bg} ${GAP_STYLES[ride.gap.status]?.border}`}>
                      <span>{GAP_STYLES[ride.gap.status]?.icon}</span>
                      <span className={GAP_STYLES[ride.gap.status]?.text}>
                        {ride.gap.gapMins} min gap
                        {ride.gap.status === "CLASH" && ` · Need ${ride.gap.requiredMins} min`}
                        {ride.gap.status === "TIGHT" && ` · Need ${ride.gap.requiredMins} min`}
                        {ride.gap.travelMins && ` · ~${ride.gap.travelMins} min travel`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 7-Day Outlook */}
      <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
        <h2 className="text-white font-semibold text-sm mb-3">Next 7 Days</h2>
        <div className="grid grid-cols-7 gap-2">
          {data.weekCounts.map(d => (
            <div key={d.date} className="text-center bg-sarathi-background/50 rounded-lg py-3 px-1">
              <p className="text-sarathi-text-muted text-xs">{d.label.split(" ")[0]}</p>
              <p className="text-white font-outfit font-bold text-lg mt-1">{d.count}</p>
              <p className="text-sarathi-text-muted text-xs">{d.label.split(" ").slice(1).join(" ")}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
